// packages/homepro/src/components/Transcript/EditBox.tsx

import React, { useEffect, useState, memo, useRef } from 'react';
import { Box, IconButton, ClickAwayListener } from '@mui/material';
import RestartAlt from '@mui/icons-material/RestartAlt';
import Save from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ReactQuill, { Quill } from 'react-quill';

import {
  ITranscriptSegment,
  ITranscriptSentence,
  INote,
} from '../../global/interfaces';
import { IDelta, IQuillClipboard } from '@homeporter/types';

import 'react-quill/dist/quill.snow.css';
import styled from '@emotion/styled';

const Delta = Quill.import('delta');

const Inline = Quill.import('blots/inline') as any;

class InsertedTagBlot extends Inline {
  static blotName = 'inserted';
  static tagName = 'span';
  static className = 'add';

  static formats(node: HTMLElement) {
    return true;
  }
}
Quill.register(InsertedTagBlot);

class DeletedTagBlot extends Inline {
  static blotName = 'deleted';
  static tagName = 'span';
  static className = 'del';

  static create(value: { content?: string }) {
    const node = super.create();
    if (value.content) {
      node.textContent = value.content;
    }
    return node;
  }

  static formats(node: HTMLElement) {
    return true;
  }
}

Quill.register(DeletedTagBlot);

const EditorWrapper = styled.div`
  .ql-container {
    /* Apply styles from the parent container */
    font-family: inherit;
    font-size: inherit;
    color: inherit;
    border: none;
    padding: 0.5rem;
  }

  .ql-editor {
    padding: 0;
    line-height: 1.5;
  }

  .add {
    background-color: #c8ffc8;
    padding: inherit;
  }

  .del {
    text-decoration: line-through;
    color: red;
    opacity: 0.7;
  }
`;

interface EditorBoxProps {
  editKey: string;
  segment: ITranscriptSegment;
  styles: Record<string, any>;
  setTranscriptEditIndex: (index: number | null) => void;
  transcriptAttachments: Record<string, (IDelta | INote)[]>;
  updateTranscriptEdits: (segment: ITranscriptSegment, text: IDelta) => void;
  currentEditRef: React.MutableRefObject<{
    [key: string]: IDelta | null;
  }>;
}

const EditBox = memo(
  ({
    editKey,
    segment,
    styles,
    setTranscriptEditIndex,
    transcriptAttachments,
    updateTranscriptEdits,
    currentEditRef,
  }: EditorBoxProps) => {
    useEffect(() => {
      console.log(':::EditBox useEffect edit: ', editKey);
    }, [editKey]);

    const [text, setText] = useState<IDelta | null>(null);
    const quillRef = useRef<ReactQuill>(null);
    const isProgrammaticChange = useRef<boolean>(false);
    const isFirstRender = useRef<boolean>(true);

    useEffect(() => {
      console.log(':::EditBox useEffect segment: ', segment);
      if (!segment) return;

      setText(
        (transcriptAttachments[editKey]?.[
          transcriptAttachments[editKey]?.length - 1
        ] as IDelta) || {
          ops: [
            {
              insert:
                segment?.sentences
                  ?.map((sentence: ITranscriptSentence) => sentence.text)
                  .join(' ') || '',
            },
          ],
        },
      );
    }, [transcriptAttachments, segment]);

    useEffect(() => {
      console.log(':::EditBox useEffect quillRef: ', quillRef.current);
      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      const handleTextChange = (delta: any, oldDelta: any, source: string) => {
        if (source !== 'user' && isProgrammaticChange.current) {
          isProgrammaticChange.current = false;
          return;
        }

        if (source !== 'user' || !delta?.ops || !oldDelta) return;

        delta.ops.forEach((op: any) => {
          if (op.delete) {
            const range = quill.getSelection();
            const deletedTextIndex = range ? range.index : 0;

            const attributes = quill.getFormat(deletedTextIndex);
            if (attributes.inserted) {
              return;
            }

            const deletedText =
              oldDelta
                .slice(deletedTextIndex, deletedTextIndex + op.delete)
                .ops?.map((deltaOp: any) => deltaOp.insert)
                .join('') || '';

            quill.insertText(deletedTextIndex, deletedText, { deleted: true });

            isProgrammaticChange.current = true;
          }
        });

        currentEditRef.current[editKey] = quillRef.current
          ?.getEditor()
          .getContents() as IDelta;
      };
      /*
      const handleSelectionChange = (
        range: any,
        oldRange: any,
        source: string,
      ) => {
        if (range) {
          console.log('Selection changed:', range);
          // Add your custom logic here
        } else {
          console.log('Selection cleared');
        }
      };
      */

      const clipboard = quill.getModule('clipboard') as IQuillClipboard;
      clipboard.addMatcher(Node.TEXT_NODE, (node: any, delta: any) => {
        delta.ops.forEach((op: any) => {
          if (op.insert) {
            op.attributes = {
              ...op.attributes,
              inserted: !isFirstRender.current,
            };
          }
        });
        isFirstRender.current = false;
        return delta;
      });

      const handleKeyDown = (event: KeyboardEvent) => {
        try {
          const quill = quillRef.current?.getEditor();
          if (!quill) return;

          if (event.key !== 'Backspace' && event.key !== 'Delete') {
            const range = quill.getSelection();

            if (range && range.length === 0 && event.key.length === 1) {
              quill.format('deleted', false);
              quill.format('inserted', true);
            } else if (range && range.length > 0) {
              const text = quill.getText(range.index, range.length);

              quill.deleteText(range.index, range.length);
              quill.insertEmbed(range.index, 'deleted', { content: text });

              quill.setSelection(range.index + text.length, 0);
              quill.format('deleted', false);
              quill.format('inserted', true);
            }
          }
        } catch (error) {
          console.error('Error in handleKeyDown:', error);
        }
      };

      const editorContainer = quill.root;
      editorContainer?.addEventListener('keydown', handleKeyDown);
      quill.on('text-change', handleTextChange);
      //quill.on('selection-change', handleSelectionChange);
      return () => {
        console.log(':::unmount quill...');
        editorContainer?.removeEventListener('keydown', handleKeyDown);
        quill.off('text-change', handleTextChange);
        //quill.off('selection-change', handleSelectionChange);
      };
    }, [quillRef.current]);

    const saveTranscriptEdits = () => {
      console.log(':::saveChanges...');
      const delta = quillRef.current?.getEditor().getContents();
      if (delta) updateTranscriptEdits(segment, delta as IDelta);
      currentEditRef.current[editKey] = null;
    };

    const resetTransciptEdits = () => {
      console.log(':::resetSegementEdits...');
      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      const resetText =
        segment?.sentences
          ?.map((sentence: ITranscriptSentence) => sentence.text)
          .join(' ') || '';

      quill.setContents(new Delta().insert(resetText));
      currentEditRef.current[editKey] = null;
    };

    useEffect(() => {
      console.log(':::EditBox useEffect text:', text);
    }, [text]);

    return (
      <ClickAwayListener
        onClickAway={() => {
          //setTranscriptEditIndex(null);
          //setTranscriptEditIndex(null);
        }}>
        <Box sx={styles.EditBox}>
          <EditorWrapper>
            <ReactQuill
              id="track-changes-editor"
              ref={quillRef}
              value={
                currentEditRef.current[editKey]
                  ? currentEditRef.current[editKey]
                  : typeof text === 'string'
                    ? new Delta().insert(text)
                    : text
              }
              modules={{
                toolbar: false,
              }}
              formats={['inserted', 'deleted']}
            />
          </EditorWrapper>

          <Box
            sx={{
              ...styles.resetContainer,
              padding: '0.25rem',
            }}>
            <IconButton
              onClick={() => {
                currentEditRef.current[editKey] = null;
                setTranscriptEditIndex(null);
              }}
              sx={{ padding: '0.25rem' }}>
              <CloseIcon fontSize="small" sx={{ color: 'gray' }} />
            </IconButton>
            {transcriptAttachments[editKey] && (
              <IconButton
                disabled={
                  text?.ops?.length && text.ops.length > 1 ? false : false
                }
                onClick={resetTransciptEdits}
                sx={{ padding: '0.25rem' }}>
                <RestartAlt fontSize="small" sx={{ color: 'gray' }} />
              </IconButton>
            )}
            <IconButton
              onClick={saveTranscriptEdits}
              sx={{ padding: '0.25rem' }}>
              <Save fontSize="small" sx={{ color: 'gray' }} />
            </IconButton>
          </Box>
        </Box>
      </ClickAwayListener>
    );
  },
);

export default EditBox;
